import {api} from '@bsky/sdk'
import {
  type InterpretedLabelValueDefinition,
  type LabelPreference,
  LABELS,
  type ModerationPrefs,
} from '@bsky/sdk/moderation'

/*
 * Sunnahsky's rules for moderation labels, decided by the owner on 2026-09-24
 * (moderation-plan.md in the workspace).
 *
 * Sunnahsky is strictly SFW and does not apply Bluesky's judgments about speech.
 * Of the labels Bluesky's moderation service applies, the app honours only the
 * ones in BLUESKY_LABEL_SETTINGS, with the setting given there. Every other
 * Bluesky label is ignored, including any Bluesky adds later, and so are
 * Bluesky moderators' own `!hide` and `!warn`. Other moderation services, such
 * as Sunnahsky's own, keep their labels, with nudity locked away and graphic
 * media behind a warning whoever applies them.
 */

const BLUESKY_MODERATION_DID = api.moderation.did

/** The Bluesky labels Sunnahsky honours, and the setting each one gets. */
export const BLUESKY_LABEL_SETTINGS: Readonly<Record<string, LabelPreference>> =
  {
    // Sexual content: hidden, and locked by being adult-only (see below)
    porn: 'hide',
    sexual: 'hide',
    'sexual-figurative': 'hide',
    nudity: 'hide',
    // Protection against junk, fraud and fake accounts
    spam: 'hide',
    scam: 'hide',
    'unsafe-link': 'hide',
    security: 'hide',
    impersonation: 'hide',
    inauthentic: 'hide',
    'engagement-farming': 'hide',
    illicit: 'hide',
    // Mature but not sexual: covered, with a button to open it
    sensitive: 'warn',
    'self-harm': 'warn',
    'graphic-media': 'warn',
  }

const BLUESKY_KEPT = new Set(Object.keys(BLUESKY_LABEL_SETTINGS))

/**
 * Labels shown behind a warning the reader can open. All are adult-only by
 * default (Bluesky marks `sensitive` and `self-harm` so, and the SDK's own table
 * marks `graphic-media` and `gore`), and the SDK locks every adult-only label
 * away while adult content is off, so the marking is removed for them.
 */
const WARN_NOT_LOCK = new Set([
  'sensitive',
  'self-harm',
  'graphic-media',
  'gore',
])

let labelTableApplied = false

/**
 * Adjusts the SDK's table of built-in labels, once. `nudity` becomes adult-only,
 * so it is locked away like `porn`; `graphic-media` and its older name `gore`
 * stop being adult-only, so they sit behind a warning; and `!hide` and `!warn`
 * become configurable, so `sunnahskyModerationPrefs` can ignore Bluesky
 * moderators' use of them. Every other service's `!hide` and `!warn` still
 * falls back to hiding or warning.
 */
export function applySunnahskyLabelTable() {
  if (labelTableApplied) return
  labelTableApplied = true
  if (!LABELS.nudity.flags.includes('adult')) {
    LABELS.nudity.flags.push('adult')
  }
  for (const identifier of ['graphic-media', 'gore'] as const) {
    LABELS[identifier].flags = LABELS[identifier].flags.filter(
      flag => flag !== 'adult',
    )
  }
  LABELS['!hide'].configurable = true
  LABELS['!warn'].configurable = true
}

/*
 * Runs when this module is first imported, which the preferences query and the
 * label definitions query both do before any moderation decision is made.
 */
applySunnahskyLabelTable()

/**
 * The account's moderation settings with Sunnahsky's rules applied. The
 * account's own settings for Bluesky's service are replaced, not merged, so a
 * dropped label can't come back from settings made in another app.
 */
export function sunnahskyModerationPrefs(
  prefs: ModerationPrefs,
): ModerationPrefs {
  const labelers = prefs.labelers.some(l => l.did === BLUESKY_MODERATION_DID)
    ? prefs.labelers
    : [...prefs.labelers, {did: BLUESKY_MODERATION_DID, labels: {}}]
  return {
    ...prefs,
    // Strictly SFW: adult content is off, whatever the account has stored.
    adultContentEnabled: false,
    labels: {
      ...prefs.labels,
      'graphic-media': 'warn',
      gore: 'warn',
      '!hide': 'hide',
      '!warn': 'warn',
    },
    labelers: labelers.map(labeler =>
      labeler.did === BLUESKY_MODERATION_DID
        ? {
            did: labeler.did,
            labels: {
              '!hide': 'ignore',
              '!warn': 'ignore',
              ...BLUESKY_LABEL_SETTINGS,
            },
          }
        : {
            ...labeler,
            labels: {...labeler.labels, 'graphic-media': 'warn', gore: 'warn'},
          },
    ),
  }
}

/**
 * The moderation services' own label definitions with Sunnahsky's rules
 * applied. Bluesky's are cut down to the labels in BLUESKY_LABEL_SETTINGS: the
 * SDK ignores a service's label that has no definition, which is what keeps
 * Bluesky's other labels, and any it adds later, off Sunnahsky.
 */
export function sunnahskyLabelDefs(
  labelDefs: Record<string, InterpretedLabelValueDefinition[]>,
): Record<string, InterpretedLabelValueDefinition[]> {
  const bluesky = labelDefs[BLUESKY_MODERATION_DID]
  if (!bluesky) return labelDefs
  return {
    ...labelDefs,
    [BLUESKY_MODERATION_DID]: bluesky
      .filter(def => BLUESKY_KEPT.has(def.identifier))
      .map(def =>
        WARN_NOT_LOCK.has(def.identifier)
          ? {...def, flags: def.flags.filter(flag => flag !== 'adult')}
          : def,
      ),
  }
}
